import type { Rule } from "../types";
import { toIsoDate } from "../util";

const EVIDENCE = "espen_2021_appetite_suppressed";

/**
 * Energy intake floor.
 *
 * Fires `bad` if today's logged kcal is below 60% of an energy floor based
 * on the athlete's lean body mass + activity. v1 uses a simple 22 kcal/kg of
 * current_weight_kg as a fallback floor when no explicit target exists.
 *
 * Rationale (ESPEN 2021): chronic energy availability < 30 kcal/kg LBM
 * suppresses appetite signaling, lowers RMR, impairs recovery. The 60%
 * threshold of the daily floor is the "definitely under-eating today"
 * bound. Fires only after at least one meal has been logged today (avoid
 * morning false-positive before breakfast).
 */
export const kcalBelowFloor: Rule = {
  id: "kcal_below_floor",
  domain: "health.nutrition",
  topics: ["kcal_target", "appetite_suppression"],
  evidence_refs: [EVIDENCE],
  title: () => "Energy intake low for the day",
  evaluate(ctx) {
    const today = toIsoDate(ctx.now);
    const roll = ctx.nutrition_rolls?.find((r) => r.date === today);
    if (!roll || roll.meals_logged === 0) return null;

    const weight = ctx.profile?.current_weight_kg;
    if (!weight) return null;

    const floorKcal = weight * 22;
    const lowerBound = floorKcal * 0.6;
    if (roll.kcal_total >= lowerBound) return null;

    // Hold off before noon. Lunch may simply not be logged yet.
    if (ctx.now.getHours() < 12) return null;

    return {
      rule_id: "kcal_below_floor",
      ts: ctx.now.toISOString(),
      tz: ctx.tz,
      severity: "warn",
      message: `${Math.round(roll.kcal_total)} kcal logged today (floor ~${Math.round(floorKcal)} based on body weight).`,
      evidence_refs: [EVIDENCE],
      factors: [
        { name: "kcal_logged", value: Math.round(roll.kcal_total), contribution: "observation", evidence_ref: EVIDENCE },
        { name: "kcal_floor", value: Math.round(floorKcal), contribution: "target", evidence_ref: EVIDENCE },
      ],
    };
  },
};
